// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../api/api.js';
import Button from '../common/Button.jsx';
import { FaUsers, FaFilter, FaSearch, FaChevronDown, FaFileCsv, FaFilePdf } from 'react-icons/fa';
import DynamicDateInput from '../common/DynamicDateInput.jsx';
import AllocationsView from './AllocationsView';
import Pagination from './Pagination';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

// Helper function to format date objects as YYYY-MM-DD strings for input fields
const formatDateForInput = (date) => date.toISOString().split('T')[0];

/**
 * Helper function to get the current date/month boundaries locked to IST (UTC + 5:30).
 * This ensures the UI defaults to the correct calendar month (e.g., Oct 1st to Oct 31st).
 */
const getIstDateBoundaries = () => {
    // Current date/time in IST
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const nowIst = new Date(new Date().getTime() + IST_OFFSET_MS);

    // Get year and month based on IST
    const year = nowIst.getUTCFullYear();
    const month = nowIst.getUTCMonth();

    // First Day of Month (IST) -> 1st day of the current month
    // We create it as a UTC date to ensure correct formatting later
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1));

    // Last Day of Month (IST) -> Last day of the current month
    const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));

    return { firstDayOfMonth, lastDayOfMonth };
};

// --- Standalone SearchableSelect Component ---
const SearchableSelect = ({ options, value, onChange, placeholder, disabled = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) { document.addEventListener("mousedown", handleClickOutside); }
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) { setTimeout(() => inputRef.current?.focus(), 100); }
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowercasedTerm = searchTerm.toLowerCase();
        return options.filter(option => (option.label || '').toLowerCase().includes(lowercasedTerm));
    }, [options, searchTerm]);

    const selectedOption = options.find(option => option.value === value);

    const handleOptionClick = (optionValue) => {
        onChange({ target: { value: optionValue } });
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className={`flex items-center justify-between p-2 border rounded-lg cursor-pointer transition-colors duration-200 bg-white ${disabled ? 'bg-gray-200' : 'hover:border-pink-500'}`} onClick={() => !disabled && setIsOpen(!isOpen)} tabIndex="0">
                <span className={`text-sm truncate ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>{selectedOption ? selectedOption.label : placeholder}</span>
                <FaChevronDown className={`transition-transform duration-200 text-gray-400 ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <AnimatePresence>
                {isOpen && !disabled && (
                    <motion.div initial={{ opacity: 0, scale: 0.95, y: -10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: -10 }} className="absolute top-full mt-1 w-full bg-white border rounded-lg shadow-lg z-50 overflow-hidden">
                        <div className="relative p-2 border-b">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input ref={inputRef} type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-8 pr-2 py-1 text-sm border-none focus:ring-0" onClick={(e) => e.stopPropagation()} />
                        </div>
                        <ul className="max-h-48 overflow-y-auto p-1">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => (<li key={option.value} onClick={() => handleOptionClick(option.value)} className="p-2 text-sm cursor-pointer hover:bg-gray-100 rounded-md">{option.label}</li>))
                            ) : (<li className="p-2 text-sm text-gray-500 italic">No options found.</li>)}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const OccupancyReport = () => {
    const [events, setEvents] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [beds, setBeds] = useState([]);
    const [error, setError] = useState(null);
    const [dateFilterType, setDateFilterType] = useState('stayRange');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    const { firstDayOfMonth, lastDayOfMonth } = useMemo(getIstDateBoundaries, []);

    const [filters, setFilters] = useState({
        eventId: '',
        buildingId: '',
        roomId: '',
        bedId: '',
        gender: '',
        startDate: formatDateForInput(firstDayOfMonth),
        endDate: formatDateForInput(lastDayOfMonth),
    });

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        limit: 25,
        totalRecords: 0,
    });

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [eventsRes, buildingsRes, roomsRes, bedsRes] = await Promise.all([
                    api.get('/events'),
                    api.get('/buildings'),
                    api.get('/rooms'),
                    api.get('/beds')
                ]);
                setEvents(eventsRes.data || []);
                setBuildings(buildingsRes.data || []);
                setRooms(roomsRes.data || []);
                setBeds(bedsRes.data || []);
            } catch (err) {
                setError('Failed to fetch filter options.');
                console.error(err);
            }
        };
        fetchDropdownData();
    }, []);

    const handleFilterChange = (e) => { // Renamed from handleDateChange
        const { name, value } = e.target;
        const inputDate = new Date(value);

        // Validate the date: Check if it's a valid date object
        // and if the date value itself matches the input to catch roll-overs (e.g. Feb 30 -> Mar 2)
        if (e.target.type === 'date' && (isNaN(inputDate.getTime()) || inputDate.toISOString().split('T')[0] !== value)) {
            console.error(`Invalid date selected for ${name}: ${value}`);
            // Optionally set an error state here to inform the user
            // For now, we will just prevent updating the state with an invalid date.
            return;
        }

        setFilters(prev => ({ ...prev, [name]: value })); // Changed setFilterDates to setFilters
    };

    const handleBuildingChange = (value) => {
        setFilters(prev => ({
            ...prev,
            buildingId: value,
            roomId: '',
            bedId: ''
        }));
    };

    const handleRoomChange = (value) => {
        setFilters(prev => ({
            ...prev,
            roomId: value,
            bedId: ''
        }));
    };

    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            currentPage: 1
        }));
    }, [
        filters.eventId,
        filters.buildingId,
        filters.roomId,
        filters.bedId,
        filters.gender,
        filters.startDate,
        filters.endDate,
        dateFilterType,
        debouncedSearchTerm
    ]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({
            ...prev,
            limit: newLimit,
            currentPage: 1,
        }));
    };

    const handleDownloadCsv = async () => {
        setIsDownloading(true);
        try {
            const params = new URLSearchParams({
                startDate: filters.startDate,
                endDate: filters.endDate,
                eventId: filters.eventId,
                buildingId: filters.buildingId,
                roomId: filters.roomId,
                bedId: filters.bedId,
                gender: filters.gender,
                dateFilterType: dateFilterType,
                searchTerm: debouncedSearchTerm,
            });

            const response = await api.get(`/people/export-csv?${params.toString()}`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'occupancy_report.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Failed to download CSV", error);
            setError("Failed to download CSV");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setIsDownloadingPdf(true);
        try {
            const params = new URLSearchParams({
                startDate: filters.startDate,
                endDate: filters.endDate,
                eventId: filters.eventId,
                buildingId: filters.buildingId,
                roomId: filters.roomId,
                bedId: filters.bedId,
                gender: filters.gender,
                dateFilterType: dateFilterType,
                searchTerm: debouncedSearchTerm,
            });

            const response = await api.get(`/people/export-pdf?${params.toString()}`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'occupancy_report.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Failed to download PDF", error);
            setError("Failed to download PDF");
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    const adjustedFilters = useMemo(() => {
        // If no adjustments are needed for the API, then adjustedFilters can simply be filters.
        // However, if other adjustments are required in the future,
        // it's good to keep the structure. For now, we pass the original dates.
        return {
            ...filters
        };
    }, [filters]);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 md:p-8 bg-neutral min-h-screen font-body">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-primaryDark border-b-4 border-primary pb-2 inline-block font-heading">
                <FaUsers className="inline mr-3 text-primary" /> Occupancy Report
            </h2>
            {error && <p className="text-red-600 bg-red-100 p-3 rounded-md mb-6">{error}</p>}

            <div className="bg-card p-4 rounded-2xl shadow-soft mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                    <h3 className="font-semibold font-heading text-lg flex items-center text-primaryDark"><FaFilter className="mr-2 text-primary" />Filters</h3>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={handleDownloadCsv} disabled={isDownloading} className="flex items-center gap-2">
                            {isDownloading ? <span className="loading loading-spinner loading-sm"></span> : <FaFileCsv />}
                            Download CSV
                        </Button>
                        <Button onClick={handleDownloadPdf} disabled={isDownloadingPdf} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white">
                            {isDownloadingPdf ? <span className="loading loading-spinner loading-sm"></span> : <FaFilePdf />}
                            Download PDF
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-4 border-b border-background mb-4">
                    <label className="font-semibold sm:col-span-1">Date Range Applies To:</label>
                    <div className="flex items-center gap-4 sm:col-span-2">
                        <label htmlFor="stayRange" className="flex items-center cursor-pointer">
                            <input type="radio" id="stayRange" name="dateFilterType" value="stayRange"
                                checked={dateFilterType === 'stayRange'}
                                onChange={(e) => setDateFilterType(e.target.value)}
                                className="h-4 w-4 text-pink-600 border-gray-300 focus:ring-pink-500" />
                            <span className="ml-2 text-gray-700">Stay Dates</span>
                        </label>
                        <label htmlFor="bookingDate" className="flex items-center cursor-pointer">
                            <input type="radio" id="bookingDate" name="dateFilterType" value="bookingDate"
                                checked={dateFilterType === 'bookingDate'}
                                onChange={(e) => setDateFilterType(e.target.value)}
                                className="h-4 w-4 text-pink-600 border-gray-300 focus:ring-pink-500" />
                            <span className="ml-2 text-gray-700">Booking Creation Dates</span>
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative md:col-span-2 lg:col-span-4">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Search by name, booking #, city..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500" />
                    </div>

                    <div className="space-y-2">
                        <DynamicDateInput
                            label="From"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            className="w-full"
                        />
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => {
                                    const { firstDayOfMonth, lastDayOfMonth } = getIstDateBoundaries();
                                    setFilters(prev => ({
                                        ...prev,
                                        startDate: formatDateForInput(firstDayOfMonth),
                                        endDate: formatDateForInput(lastDayOfMonth)
                                    }));
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                            >
                                This Month
                            </button>
                            <button
                                onClick={() => {
                                    const today = new Date();
                                    const todayStr = today.toISOString().split('T')[0];
                                    setFilters(prev => ({ ...prev, startDate: todayStr, endDate: todayStr }));
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                            >
                                Today
                            </button>
                            <button
                                onClick={() => {
                                    const start = new Date();
                                    const end = new Date();
                                    end.setDate(end.getDate() + 7);
                                    setFilters(prev => ({
                                        ...prev,
                                        startDate: start.toISOString().split('T')[0],
                                        endDate: end.toISOString().split('T')[0]
                                    }));
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                            >
                                7 Days
                            </button>
                            <button
                                onClick={() => {
                                    const start = new Date();
                                    const end = new Date();
                                    end.setDate(end.getDate() + 30);
                                    setFilters(prev => ({
                                        ...prev,
                                        startDate: start.toISOString().split('T')[0],
                                        endDate: end.toISOString().split('T')[0]
                                    }));
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                            >
                                30 Days
                            </button>
                        </div>
                    </div>

                    <DynamicDateInput
                        label="To"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        className="w-full"
                    />

                    <SearchableSelect
                        options={[{ value: '', label: 'All Events' }, ...events.map(e => ({ value: e._id, label: e.name }))]}
                        value={filters.eventId}
                        onChange={(e) => handleFilterChange({ target: { name: 'eventId', value: e.target.value } })}
                        placeholder="All Events"
                    />
                    <SearchableSelect
                        options={[{ value: '', label: 'All Buildings' }, ...buildings.map(b => ({ value: b._id, label: b.name }))]}
                        value={filters.buildingId}
                        onChange={(e) => handleBuildingChange(e.target.value)}
                        placeholder="All Buildings"
                    />
                    <SearchableSelect
                        options={[
                            { value: '', label: 'All Rooms' },
                            ...(filters.buildingId
                                ? rooms
                                    .filter(r => {
                                        // Safely handle both populated object and string ID
                                        const bId = r.buildingId?._id || r.buildingId;
                                        return bId && String(bId) === String(filters.buildingId);
                                    })
                                    .map(r => ({ value: r._id, label: `Room ${r.roomNumber}` }))
                                : [])
                        ]}
                        value={filters.roomId}
                        onChange={(e) => handleRoomChange(e.target.value)}
                        placeholder="All Rooms"
                        disabled={!filters.buildingId}
                    />
                    <SearchableSelect
                        options={[
                            { value: '', label: 'All Beds' },
                            ...(filters.roomId
                                ? beds
                                    .filter(b => {
                                        // Safely handle both populated object and string ID
                                        const rId = b.roomId?._id || b.roomId;
                                        return rId && String(rId) === String(filters.roomId);
                                    })
                                    .map(b => ({ value: b._id, label: b.name || 'Bed' }))
                                : [])
                        ]}
                        value={filters.bedId}
                        onChange={(e) => handleFilterChange({ target: { name: 'bedId', value: e.target.value } })}
                        placeholder="All Beds"
                        disabled={!filters.roomId}
                    />
                    <select name="gender" value={filters.gender} onChange={handleFilterChange} className="p-2 border rounded-lg bg-white">
                        <option value="">All Genders</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Boy">Boy</option>
                        <option value="Girl">Girl</option>
                    </select>
                </div>
            </div>

            <AllocationsView
                filters={adjustedFilters}
                dateFilterType={dateFilterType}
                debouncedSearchTerm={debouncedSearchTerm}
                pagination={pagination}
                setPagination={setPagination}
            />

            <Pagination pagination={pagination} onPageChange={handlePageChange} onLimitChange={handleLimitChange} />
        </motion.div>
    );
};

export default OccupancyReport;
